import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  Card, Typography, Tag, Tabs, Select, Row, Col, Statistic, Input, DatePicker,
  InputNumber, Space, Button, message, Modal, Table, Popconfirm,
} from '../../ui';
import type { TagTone } from '../../ui';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Pencil } from 'lucide-react';
import dayjs, { Dayjs } from 'dayjs';
import { disableFuture } from '../../lib/dateRange';
import { request } from '../../api/client';
import { TwDataTable as DataTable, type DataTableColumn } from '../../components/TwDataTable';
import { usePermission, useAuthStore } from '../../store/auth';
import { useDebounce } from '../../hooks/useDebounce';
import { formatIstDate } from '../../lib/time';
import { FileUploadField } from '../../components/FileUploadField';
import { stateName } from '../../constants/indianStates';
import { EditReportModal } from './EditReportModal';

const STATUS_COLOR: Record<string, TagTone> = {
  DRAFT: 'neutral',
  SUBMITTED: 'gold',
  APPROVED: 'cyan',
  CLOSED: 'green',
  CORRECTION_REQUIRED: 'gold',
  REJECTED: 'red',
};

const inr = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const inr2 = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

// Short role labels for the "by-whom" capsules.
const ROLE_LABEL: Record<string, string> = {
  SALES_PERSON: 'SP', SALES_HEAD: 'SH', ACCOUNTS: 'Accounts', ADMIN: 'Admin', SUPER_ADMIN: 'SA',
};
const roleTag = (role?: string | null) =>
  role ? <Tag tone="blue" style={{ marginInlineEnd: 0 }}>{ROLE_LABEL[role] ?? role.replace(/_/g, ' ')}</Tag> : null;

// A tab label with a trailing count chip (shown only when there's something to count).
const tabWithCount = (text: string, count?: number, tone?: TagTone) => (
  <Space size={6}>{text}{count ? <Tag tone={tone} style={{ marginInlineEnd: 0 }}>{count}</Tag> : null}</Space>
);

const CATEGORY_OPTIONS = [
  'TRAVEL', 'FOOD', 'LOCAL_CONVEYANCE', 'HOTEL', 'FUEL',
  'TOLL_PARKING', 'TRAIN', 'FLIGHT', 'BUS', 'OTHER',
].map((c) => ({ value: c, label: c.replace(/_/g, ' ') }));

// HOTEL and OTHER require a description (server-enforced — mirror it client-side).
const DESC_REQUIRED = new Set(['HOTEL', 'OTHER']);

const PAYMENT_MODE_OPTIONS = [
  'CASH', 'UPI', 'BANK_TRANSFER', 'CHEQUE', 'CARD', 'OTHER',
].map((m) => ({ value: m, label: m.replace(/_/g, ' ') }));

const VISIT_TYPE_OPTIONS = [
  'FIELD_VISIT', 'TOUR', 'OFFICE', 'OTHER',
].map((v) => ({ value: v, label: v.replace(/_/g, ' ') }));

const TRAVEL_MODE_OPTIONS = [
  'CAR', 'BIKE', 'TRAIN', 'FLIGHT', 'BUS', 'TAXI', 'OTHER',
].map((v) => ({ value: v, label: v.replace(/_/g, ' ') }));

// ── Editable item row used by the modal ──
interface DraftItem {
  key: string;
  expenseDate: Dayjs | null;
  category?: string;
  description?: string;
  amount?: number;
  vendorName?: string;
  billUrl?: string;
}

const blankItem = (): DraftItem => ({
  key: Math.random().toString(36).slice(2),
  expenseDate: dayjs(),
});

interface FileExpenseReportModalProps {
  open: boolean;
  onClose: () => void;
  visitId?: string;
  onCreated?: () => void;
}

export function FileExpenseReportModal({ open, onClose, visitId, onCreated }: FileExpenseReportModalProps) {
  const navigate = useNavigate();

  // Header fields.
  const [visitType, setVisitType] = useState<string | undefined>();
  const [partyName, setPartyName] = useState('');
  const [partyContact, setPartyContact] = useState('');
  const [city, setCity] = useState('');
  const [travelMode, setTravelMode] = useState<string | undefined>();
  const [notes, setNotes] = useState('');

  const [items, setItems] = useState<DraftItem[]>([blankItem()]);

  // Idempotency key for THIS file-report session — a retry / double-click / token-refresh
  // re-send carries the same ref, so the server returns the same report instead of a duplicate.
  // A fresh modal open mints a new ref (so a genuinely new report is created).
  const clientRef = useMemo(() => (open ? `er-${Date.now()}-${Math.random().toString(36).slice(2, 10)}` : ''), [open]);

  // Who the report is for. A manager/admin files ON BEHALF OF a sales person (when not
  // already tied to a visit); a sales person files for themselves. The party is then a
  // real retailer/farmer (scoped to that sales person), not free text.
  const isManager = usePermission('EXPENSE.APPROVE.HEAD') || usePermission('EXPENSE.APPROVE.ACCT');
  const onBehalf = isManager && !visitId;
  const [repId, setRepId] = useState<string | undefined>();
  const [partyId, setPartyId] = useState<string | undefined>();
  const [partyAddress, setPartyAddress] = useState('');
  const [stateVal, setStateVal] = useState('');
  const [pincode, setPincode] = useState('');

  const { data: salesPersons } = useQuery({
    queryKey: ['report-engineers'],
    queryFn: () => request<any[]>({ url: '/wallets' }),
    enabled: open && onBehalf,
  });
  // Both lists are fetched once we know whose report it is (scoped to that sales person),
  // then merged into a single party picker — no need to choose a visit type first.
  const partyReady = !onBehalf || !!repId;
  const { data: retailers } = useQuery({
    queryKey: ['report-retailers', repId],
    queryFn: () => request<any>({ url: '/retailers', params: { pageSize: 200, ...(repId ? { assignedSalesPersonId: repId } : {}) } }),
    enabled: open && partyReady,
  });
  const { data: farmers } = useQuery({
    queryKey: ['report-farmers', repId],
    queryFn: () => request<any>({ url: '/farmers', params: { pageSize: 200, ...(repId ? { assignedSalesPersonId: repId } : {}) } }),
    enabled: open && partyReady,
  });
  const retailerList: any[] = (retailers as any)?.data ?? (Array.isArray(retailers) ? retailers : []);
  const farmerList: any[] = (farmers as any)?.data ?? (Array.isArray(farmers) ? farmers : []);

  // One combined party dropdown (retailers + farmers under the rep), each with a capsule.
  const partyOptions = useMemo(() => ([
    ...retailerList.map((r: any) => ({
      value: `R:${r.id}`, kind: 'RETAILER', searchText: `${r.companyName ?? ''} ${r.city ?? ''} ${r.mobile ?? ''}`,
      label: <Space size={6}>{r.companyName}<Tag tone="blue">Retailer</Tag></Space>,
    })),
    ...farmerList.map((f: any) => ({
      value: `F:${f.id}`, kind: 'FARMER', searchText: `${f.fullName ?? ''} ${f.city ?? ''} ${f.mobile ?? ''}`,
      label: <Space size={6}>{f.fullName}<Tag tone="green">Farmer</Tag></Space>,
    })),
  ]), [retailerList, farmerList]);

  // Pick a party → freeze its snapshot (name, contact, address, city, state, pincode).
  const pickParty = (value: string) => {
    setPartyId(value);
    const [k, rid] = value.split(':');
    if (k === 'R') {
      const r = retailerList.find((x: any) => String(x.id) === rid);
      if (r) { setPartyName(r.companyName ?? ''); setPartyContact(r.mobile ?? r.contactPerson ?? ''); setCity(r.city ?? ''); setStateVal(stateName(r.stateCode)); setPincode(r.pincode ?? ''); setPartyAddress([r.addressLine1, r.addressLine2].filter(Boolean).join(', ')); }
    } else {
      const f = farmerList.find((x: any) => String(x.id) === rid);
      if (f) { setPartyName(f.fullName ?? ''); setPartyContact(f.mobile ?? ''); setCity(f.city ?? ''); setStateVal(stateName(f.stateCode)); setPincode(f.pincode ?? ''); setPartyAddress(f.address ?? ''); }
    }
  };

  const reset = () => {
    setVisitType(undefined);
    setPartyName('');
    setPartyContact('');
    setCity('');
    setTravelMode(undefined);
    setNotes('');
    setRepId(undefined);
    setPartyId(undefined);
    setPartyAddress('');
    setStateVal('');
    setPincode('');
    setItems([blankItem()]);
  };

  const closeAndReset = () => { reset(); onClose(); };

  const patchItem = (key: string, patch: Partial<DraftItem>) =>
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  const addItem = () => setItems((prev) => [...prev, blankItem()]);
  const removeItem = (key: string) =>
    setItems((prev) => (prev.length > 1 ? prev.filter((it) => it.key !== key) : prev));

  const subtotal = useMemo(
    () => items.reduce((s, it) => s + Number(it.amount ?? 0), 0),
    [items],
  );

  const create = useMutation({
    mutationFn: (submit: boolean) => buildAndCreate(submit),
    onSuccess: (_d, submit) => {
      message.success(submit ? 'Expense report submitted' : 'Draft saved');
      onCreated?.();
      closeAndReset();
    },
    onError: (e: any) => {
      const err = e?.response?.data?.error;
      const dupId = err?.details?.reportId;
      // One report per rep per visit — if filing from a visit that already has one, offer
      // to continue that draft instead of failing.
      if (err?.code === 'DUPLICATE_REPORT' && dupId) {
        Modal.confirm({
          title: 'A report already exists for this visit',
          content: 'Continue editing the existing report instead of starting a new one?',
          okText: 'Open existing report',
          cancelText: 'Cancel',
          onOk: () => { closeAndReset(); navigate(`/expense-reports/${dupId}`); },
        });
        return;
      }
      message.error(err?.message ?? e?.message ?? 'Could not save report');
    },
  });

  async function buildAndCreate(submit: boolean) {
    if (!visitType) throw new Error('Pick a visit type');
    if (onBehalf && !repId) throw new Error('Pick the sales person this report is for');
    const filled = items.filter((it) => it.category || it.amount != null || it.description || it.vendorName);
    if (!filled.length) throw new Error('Add at least one expense line');
    for (const it of filled) {
      if (!it.category) throw new Error('Every line needs a category');
      if (it.amount == null || Number(it.amount) <= 0) throw new Error('Every line needs an amount');
      if (DESC_REQUIRED.has(it.category) && !it.description?.trim()) {
        throw new Error(`${it.category} lines need a description`);
      }
    }
    const payload: any = {
      visitType,
      ...(clientRef ? { clientRef } : {}),
      ...(visitId ? { visitId } : {}),
      ...(repId ? { repId } : {}),
      ...(partyName.trim() ? { partyName: partyName.trim() } : {}),
      ...(partyContact.trim() ? { partyContact: partyContact.trim() } : {}),
      ...(partyAddress.trim() ? { partyAddress: partyAddress.trim() } : {}),
      ...(city.trim() ? { city: city.trim() } : {}),
      ...(stateVal.trim() ? { state: stateVal.trim() } : {}),
      ...(pincode.trim() ? { pincode: pincode.trim() } : {}),
      ...(travelMode ? { travelMode } : {}),
      ...(notes.trim() ? { notes: notes.trim() } : {}),
      items: filled.map((it) => ({
        category: it.category,
        amount: Number(it.amount),
        ...(it.expenseDate ? { expenseDate: it.expenseDate.format('YYYY-MM-DD') } : {}),
        ...(it.description?.trim() ? { description: it.description.trim() } : {}),
        ...(it.vendorName?.trim() ? { vendorName: it.vendorName.trim() } : {}),
        ...(it.billUrl ? { billUrl: it.billUrl } : {}),
      })),
    };
    const created = await request<any>({ url: '/expense-reports', method: 'POST', data: payload });
    if (submit && created?.id != null) {
      await request({ url: `/expense-reports/${created.id}/submit`, method: 'PATCH' });
    }
    return created;
  }

  const itemColumns = [
    {
      title: 'Date', dataIndex: 'expenseDate', width: 140,
      render: (_: any, r: DraftItem) => (
        <DatePicker
          size="small" className="w-full" value={r.expenseDate}
          onChange={(d) => patchItem(r.key, { expenseDate: d })}
        />
      ),
    },
    {
      title: 'Category', dataIndex: 'category', width: 160,
      render: (_: any, r: DraftItem) => (
        <Select
          size="small" className="w-full" placeholder="Category"
          value={r.category} onChange={(v) => patchItem(r.key, { category: v })}
          options={CATEGORY_OPTIONS}
        />
      ),
    },
    {
      title: 'Description', dataIndex: 'description', width: 200,
      render: (_: any, r: DraftItem) => (
        <Input
          size="sm"
          placeholder={r.category && DESC_REQUIRED.has(r.category) ? 'Required' : 'Optional'}
          invalid={!!(r.category && DESC_REQUIRED.has(r.category) && !r.description?.trim())}
          value={r.description} onChange={(e) => patchItem(r.key, { description: e.target.value })}
        />
      ),
    },
    {
      title: 'Amount', dataIndex: 'amount', width: 120, align: 'right' as const,
      render: (_: any, r: DraftItem) => (
        <InputNumber
          size="small" style={{ width: '100%' }} min={0} placeholder="₹"
          value={r.amount} onChange={(v) => patchItem(r.key, { amount: v == null ? undefined : Number(v) })}
        />
      ),
    },
    {
      title: 'Vendor', dataIndex: 'vendorName', width: 150,
      render: (_: any, r: DraftItem) => (
        <Input
          size="sm" placeholder="Vendor"
          value={r.vendorName} onChange={(e) => patchItem(r.key, { vendorName: e.target.value })}
        />
      ),
    },
    {
      title: 'Bill', dataIndex: 'billUrl', width: 220,
      render: (_: any, r: DraftItem) => (
        <FileUploadField
          size="small" category="receipt" preview={false} buttonLabel={r.billUrl ? 'Replace' : 'Upload'}
          value={r.billUrl} onChange={(url) => patchItem(r.key, { billUrl: url })}
        />
      ),
    },
    {
      title: '', dataIndex: 'key', width: 40,
      render: (_: any, r: DraftItem) => (
        <Button
          size="sm" variant="text" aria-label="Remove line" icon={<Trash2 size={16} className="text-danger-600 dark:text-danger-500" />}
          disabled={items.length <= 1} onClick={() => removeItem(r.key)}
        />
      ),
    },
  ];

  return (
    <Modal
      open={open}
      title="File expense report"
      width={1000}
      maskClosable={false}
      onOpenChange={(o) => { if (!o) closeAndReset(); }}
      footer={[
        <Button key="cancel" onClick={closeAndReset} disabled={create.isPending}>Cancel</Button>,
        <Button
          key="draft"
          onClick={() => create.mutate(false)}
          loading={create.isPending && create.variables === false}
        >
          Save draft
        </Button>,
        <Button
          key="submit" variant="primary"
          onClick={() => create.mutate(true)}
          loading={create.isPending && create.variables === true}
        >
          Save &amp; submit
        </Button>,
      ]}
    >
      {onBehalf && (
        <Row gutter={[12, 10]} style={{ marginBottom: 18 }}>
          <Col xs={24} md={12}>
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Sales person (filing on behalf) *</Typography.Text>
            <Select
              showSearch optionFilterProp="label" className="w-full" placeholder="Select sales person"
              value={repId} onChange={(v) => { setRepId(v); setPartyId(undefined); setPartyName(''); setPartyContact(''); setCity(''); setStateVal(''); setPincode(''); setPartyAddress(''); }}
              options={(salesPersons ?? []).map((s: any) => ({ value: String(s.repId), label: s.repName ?? `#${s.repId}` }))}
            />
          </Col>
        </Row>
      )}
      <Row gutter={[12, 10]} style={{ marginBottom: 18 }}>
        <Col xs={24} md={8}>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Visit type *</Typography.Text>
          <Select
            className="w-full" placeholder="Visit type" value={visitType}
            onChange={setVisitType} options={VISIT_TYPE_OPTIONS}
          />
        </Col>
        <Col xs={24} md={8}>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Travel mode</Typography.Text>
          <Select
            className="w-full" placeholder="Travel mode" allowClear value={travelMode}
            onChange={setTravelMode} options={TRAVEL_MODE_OPTIONS}
          />
        </Col>
        <Col xs={24} md={8}>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Territory</Typography.Text>
          <Input disabled placeholder="From the selected party" value={city ? `${city}${stateVal ? ` · ${stateVal}` : ''}` : (stateVal || '')} />
        </Col>
      </Row>
      <Row gutter={[12, 10]} style={{ marginBottom: 18 }}>
        <Col xs={24} md={8}>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Party (retailer / farmer){onBehalf && !repId ? ' — pick sales person first' : ''}</Typography.Text>
          <Select
            showSearch className="w-full" placeholder="Select retailer or farmer"
            value={partyId} onChange={pickParty} disabled={onBehalf && !repId}
            notFoundContent={onBehalf && !repId ? 'Pick a sales person first' : 'No retailers / farmers under this sales person'}
            filterOption={(input, opt) => String((opt as any)?.searchText ?? '').toLowerCase().includes(input.toLowerCase())}
            options={partyOptions as any}
          />
        </Col>
        <Col xs={24} md={8}>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Party contact</Typography.Text>
          <Input placeholder="Phone / contact" value={partyContact} onChange={(e) => setPartyContact(e.target.value)} />
        </Col>
        <Col xs={24} md={8}>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Notes</Typography.Text>
          <Input placeholder="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Col>
      </Row>

      <Table<DraftItem>
        rowKey="key"
        size="small"
        pagination={false}
        dataSource={items}
        columns={itemColumns as any}
        scroll={{ x: 980 }}
        footer={() => (
          <Space style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button size="sm" icon={<Plus size={16} />} onClick={addItem}>Add line</Button>
            <Typography.Text strong>Subtotal: {inr2(subtotal)}</Typography.Text>
          </Space>
        )}
      />
    </Modal>
  );
}

interface Props {
  /** Defaults: 'all'. Approver landing → 'SUBMITTED'. Accounts landing → 'APPROVED' (to pay). */
  defaultStatus?: string;
  title?: string;
}

export function ExpenseReportsPage({ defaultStatus, title }: Props) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canCreate = usePermission('EXPENSE.CREATE');
  const canApproveHead = usePermission('EXPENSE.APPROVE.HEAD');
  const canApproveAcct = usePermission('EXPENSE.APPROVE.ACCT');
  const isManager = canApproveHead || canApproveAcct;
  const myId = useAuthStore((s) => s.user?.id);

  const [status, setStatus] = useState<string | undefined>(defaultStatus);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 400);
  const [repId, setRepId] = useState<string | undefined>();
  const [visitType, setVisitType] = useState<string | undefined>();
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [rejectFor, setRejectFor] = useState<{ id: string; action: string; label: string } | null>(null);
  const [editId, setEditId] = useState<string | null>(null);

  const params = useMemo(() => {
    const p: Record<string, any> = {};
    if (status) p.status = status;
    if (search.trim()) p.search = search.trim();
    if (repId) p.repId = repId;
    if (visitType) p.visitType = visitType;
    if (range?.[0] && range?.[1]) { p.from = range[0].format('YYYY-MM-DD'); p.to = range[1].format('YYYY-MM-DD'); }
    return p;
  }, [status, search, repId, visitType, range]);
  // Counts drop the status tab, so each tab shows how many match the OTHER active filters —
  // accurate regardless of which tab is selected (the filtered list alone can't tell us this).
  const countParams = useMemo(() => { const p = { ...params }; delete p.status; return p; }, [params]);

  const { data, isLoading } = useQuery({
    queryKey: ['expense-reports', params],
    queryFn: () => request<any[]>({ url: '/expense-reports', params }),
    placeholderData: keepPreviousData,
  });
  const { data: forCounts } = useQuery({
    queryKey: ['expense-reports', 'counts', countParams],
    queryFn: () => request<any[]>({ url: '/expense-reports', params: countParams }),
    placeholderData: keepPreviousData,
  });
  const statusCounts = useMemo(() => {
    const m: Record<string, number> = {};
    for (const r of forCounts ?? []) m[r.status] = (m[r.status] ?? 0) + 1;
    return m;
  }, [forCounts]);
  // Sales-person dropdown for the Engineer filter (managers/admins only).
  const { data: engineers } = useQuery({
    queryKey: ['report-engineers'],
    queryFn: () => request<any[]>({ url: '/wallets' }),
    enabled: isManager,
  });
  const act = useMutation({
    mutationFn: ({ id, action, data }: { id: string; action: string; data?: any }) =>
      request({ url: `/expense-reports/${id}/${action}`, method: 'PATCH', data }),
    onSuccess: () => { message.success('Done'); qc.invalidateQueries({ queryKey: ['expense-reports'] }); qc.invalidateQueries({ queryKey: ['wallets'] }); qc.invalidateQueries({ queryKey: ['badges'] }); },
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Action failed'),
  });
  const del = useMutation({
    mutationFn: (id: string) => request({ url: `/expense-reports/${id}`, method: 'DELETE' }),
    onSuccess: () => { message.success('Draft deleted'); qc.invalidateQueries({ queryKey: ['expense-reports'] }); qc.invalidateQueries({ queryKey: ['badges'] }); },
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Could not delete'),
  });

  const rows = data ?? [];

  const queue = useMemo(() => ({
    awaitingApproval: statusCounts.SUBMITTED ?? 0,
    awaitingClose: statusCounts.APPROVED ?? 0,
  }), [statusCounts]);

  const columns: DataTableColumn<any>[] = [
    { key: 'reportNo', title: 'Report No', dataIndex: 'reportNo', sortable: true, alwaysVisible: true },
    {
      key: 'repName', title: 'Sales Person', dataIndex: 'repName', sortable: true, width: 170,
      render: (v: any, r: any) => v ? <Tag tone="blue">{v}</Tag> : `#${r.repId}`,
    },
    {
      key: 'partyName', title: 'Party / Visit', dataIndex: 'partyName', sortable: true,
      render: (v: any, r: any) => (
        <Space size={4} wrap>
          {v ? <span>{v}</span> : <span style={{ color: '#ccc' }}>—</span>}
          {r.visitType && <Tag>{String(r.visitType).replace(/_/g, ' ')}</Tag>}
        </Space>
      ),
    },
    {
      key: 'subtotalAmount', title: 'Subtotal', dataIndex: 'subtotalAmount',
      sortable: true, align: 'right', alwaysVisible: true, render: (v: any) => <b>{inr(v)}</b>,
    },
    {
      key: 'paidAmount', title: 'Approved amt', dataIndex: 'paidAmount', sortable: true, align: 'right',
      defaultVisible: false,
      render: (v: any) => Number(v) > 0
        ? <span style={{ color: '#1F8A4C' }}>{inr(v)}</span>
        : <span style={{ color: '#ccc' }}>—</span>,
    },
    {
      key: 'status', title: 'Status', dataIndex: 'status', sortable: true,
      render: (s: string) => <Tag tone={STATUS_COLOR[s] ?? 'neutral'}>{(s ?? '').replace(/_/g, ' ')}</Tag>,
    },
    {
      key: 'submittedAt', title: 'Submitted', dataIndex: 'submittedAt', sortable: true,
      render: (v: string, r: any) => formatIstDate(v ?? r.createdAt),
    },
    {
      key: 'submittedByName', title: 'Submitted by', dataIndex: 'submittedByName', sortable: true, width: 190, alwaysVisible: true,
      render: (v: any, r: any) => v
        ? <Space size={4} wrap>{v}{roleTag(r.submittedByRole)}</Space>
        : <span style={{ color: '#ccc' }}>—</span>,
    },
    {
      key: 'createdByName', title: 'Filed by', dataIndex: 'createdByName', sortable: true, width: 170, defaultVisible: false,
      render: (v: any) => v ?? '—',
    },
    {
      key: 'approvedByName', title: 'Approved by', dataIndex: 'approvedByName', sortable: true, width: 190,
      render: (v: any, r: any) => (
        r.status === 'REJECTED'
          ? <span style={{ color: '#D9363E' }}>Rejected by {v ?? '—'}{r.approvedAt && <span style={{ color: '#888', fontSize: 12 }}> · {formatIstDate(r.approvedAt)}</span>}</span>
          : v
            ? <Space direction="vertical" size={0}>
                <Space size={4} wrap><span style={{ color: '#1F8A4C' }}>{v}</span>{roleTag(r.approvedByRole)}</Space>
                {r.approvedAt && <span style={{ fontSize: 12, color: '#888' }}>{formatIstDate(r.approvedAt)}</span>}
              </Space>
            : <span style={{ color: '#ccc' }}>—</span>
      ),
    },
    {
      key: 'actions', title: 'Action', dataIndex: 'id', alwaysVisible: true, width: 300,
      render: (_: any, r: any) => {
        const id = String(r.id);
        const mine = String(r.repId) === String(myId);
        // Editable only by the beneficiary (owner) or the filer (creator), while draft / sent back.
        const canEditDraft = ['DRAFT', 'CORRECTION_REQUIRED'].includes(r.status) && (mine || String(r.createdBy) === String(myId));
        const stop = (e: any) => e.stopPropagation();
        const btns: any[] = [
          <Button key="o" size="sm" onClick={(e) => { stop(e); navigate(`/expense-reports/${id}`); }}>Open</Button>,
        ];
        if (canEditDraft) {
          btns.push(
            <Button key="edit" size="sm" variant="ghost" icon={<Pencil size={16} />}
              onClick={(e) => { stop(e); setEditId(id); }}>Edit</Button>,
            <Popconfirm key="su" title="Submit this report for approval?" okText="Submit"
              onConfirm={() => act.mutate({ id, action: 'submit' })}>
              <Button size="sm" variant="primary" onClick={stop}>Submit</Button>
            </Popconfirm>,
          );
        }
        // Single approval layer — any approver approves / rejects a submitted report.
        if (r.status === 'SUBMITTED' && isManager) {
          btns.push(
            <Popconfirm key="ap" title="Approve this report?" okText="Approve"
              onConfirm={() => act.mutate({ id, action: 'approve' })}>
              <Button size="sm" variant="primary" onClick={stop}>Approve</Button>
            </Popconfirm>,
            <Button key="rj" size="sm" variant="danger" onClick={(e) => { stop(e); setRejectFor({ id, action: 'reject', label: 'Reject report' }); }}>Reject</Button>,
          );
        }
        if (r.status === 'APPROVED' && canApproveAcct) {
          btns.push(
            <Popconfirm key="cl" title="Close this report?" description="This finishes the report." okText="Close"
              onConfirm={() => act.mutate({ id, action: 'close' })}>
              <Button size="sm" variant="primary" onClick={stop}>Close</Button>
            </Popconfirm>,
          );
        }
        // Discard an unwanted draft (owner or the manager who filed it).
        if (canEditDraft) {
          btns.push(
            <Popconfirm key="del" title="Delete this draft? This can't be undone." okText="Delete" okButtonProps={{ danger: true }}
              onConfirm={() => del.mutate(id)}>
              <Button size="sm" variant="danger" onClick={stop}>Delete</Button>
            </Popconfirm>,
          );
        }
        return <Space size={4} wrap onClick={stop}>{btns}</Space>;
      },
    },
  ];

  return (
    <div>
      {/* Header row — page title + primary actions, above the filters. */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Typography.Title level={4} style={{ margin: 0 }}>{title ?? 'Expense Reports'}</Typography.Title>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {canCreate && (
            <Button variant="primary" size="sm" icon={<Plus size={16} />} onClick={() => setModalOpen(true)}>
              File report
            </Button>
          )}
        </div>
      </div>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} md={8}>
          <Card><Statistic title="Awaiting approval" value={queue.awaitingApproval}
            valueStyle={{ color: queue.awaitingApproval > 0 ? '#E7B22C' : undefined }} /></Card>
        </Col>
        <Col xs={12} md={8}>
          <Card><Statistic title="Approved (to close)" value={queue.awaitingClose}
            valueStyle={{ color: queue.awaitingClose > 0 ? '#13C2C2' : undefined }} /></Card>
        </Col>
        <Col xs={12} md={8}>
          <Card><Statistic title="Total (filtered)" value={forCounts?.length ?? rows.length} /></Card>
        </Col>
      </Row>

      <Card>
        <Tabs
          activeKey={status ?? 'all'}
          onChange={(k) => setStatus(k === 'all' ? undefined : k)}
          items={[
            { key: 'all', label: tabWithCount('All', forCounts?.length) },
            { key: 'DRAFT', label: tabWithCount('Drafts', statusCounts.DRAFT) },
            { key: 'SUBMITTED', label: tabWithCount('⏳ Awaiting approval', statusCounts.SUBMITTED, 'gold') },
            { key: 'APPROVED', label: tabWithCount('⏳ Approved', statusCounts.APPROVED, 'cyan') },
            { key: 'CLOSED', label: tabWithCount('✓ Closed', statusCounts.CLOSED) },
            { key: 'CORRECTION_REQUIRED', label: tabWithCount('Correction required', statusCounts.CORRECTION_REQUIRED, 'gold') },
            { key: 'REJECTED', label: tabWithCount('Rejected', statusCounts.REJECTED, 'red') },
          ]}
        />
        <DataTable
          storageKey="expense-reports"
          rowKey="id"
          loading={isLoading}
          dataSource={rows}
          columns={columns}
          emptyText="No expense reports found"
          onRow={(r: any) => ({
            onClick: () => navigate(`/expense-reports/${r.id}`),
            style: { cursor: 'pointer' },
          })}
          pagination={{ pageSize: 25, showSizeChanger: true }}
          toolbarLeft={
            <Space wrap>
              <Input
                placeholder="Report no / party" style={{ width: 200 }}
                value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
              />
              {isManager && (
                <Select placeholder="Sales person" allowClear showSearch optionFilterProp="label" style={{ width: 180 }}
                  value={repId} onChange={setRepId}
                  options={(engineers ?? []).map((e: any) => ({ value: String(e.repId), label: e.repName ?? `#${e.repId}` }))} />
              )}
              <Select placeholder="Visit type" allowClear style={{ width: 150 }} value={visitType} onChange={setVisitType}
                options={VISIT_TYPE_OPTIONS} />
              <DatePicker.RangePicker disabledDate={disableFuture} value={range} onChange={(r) => setRange(r as [Dayjs, Dayjs] | null)} />
              {(searchInput || repId || visitType || range) && (
                <Button variant="text" onClick={() => { setSearchInput(''); setRepId(undefined); setVisitType(undefined); setRange(null); }}>Clear</Button>
              )}
            </Space>
          }
        />
      </Card>

      <FileExpenseReportModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={() => qc.invalidateQueries({ queryKey: ['expense-reports'] })}
      />
      {rejectFor && (
        <ReasonModal title={rejectFor.label} onClose={() => setRejectFor(null)}
          onSubmit={(reason) => { act.mutate({ id: rejectFor.id, action: rejectFor.action, data: { reason } }); setRejectFor(null); }} />
      )}
      {editId && (
        <EditReportModal
          reportId={editId} onClose={() => setEditId(null)}
          onSaved={() => { qc.invalidateQueries({ queryKey: ['expense-reports'] }); qc.invalidateQueries({ queryKey: ['expense-report', editId] }); }}
        />
      )}
    </div>
  );
}

// Reason capture for a manager/admin reject (reason is required by the backend).
function ReasonModal({ title, onClose, onSubmit }: { title: string; onClose: () => void; onSubmit: (reason: string) => void }) {
  const [reason, setReason] = useState('');
  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose(); }} title={title}
      footer={<>
        <Button variant="default" onClick={onClose}>Cancel</Button>
        <Button variant="danger" disabled={!reason.trim()} onClick={() => onSubmit(reason.trim())}>Confirm</Button>
      </>}>
      <Input.TextArea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (visible to the sales person)" />
    </Modal>
  );
}
